import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { validateAppointmentToken } from '../../firebase/appointments';
import { formatDate } from '../../utils/dateUtils';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

/**
 * Página para cliente reagendar agendamento através do link enviado por WhatsApp
 * @returns {JSX.Element} Componente React
 */
const ReschedulePage = () => {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // Estados
  const [status, setStatus] = useState('loading'); // loading, valid, invalid, rescheduled, error
  const [appointment, setAppointment] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Datas e horários disponíveis (exemplo simplificado)
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  
  // Validar token e carregar agendamento
  useEffect(() => {
    const validateAndLoad = async () => {
      try {
        if (!appointmentId || !token) {
          setStatus('invalid');
          setErrorMessage('Link inválido ou expirado. Por favor, entre em contato com o estabelecimento.');
          return;
        }
        
        const appointmentData = await validateAppointmentToken(appointmentId, token);
        
        if (!appointmentData) {
          setStatus('invalid');
          setErrorMessage('Link inválido ou expirado. Por favor, entre em contato com o estabelecimento.');
          return;
        }
        
        setAppointment(appointmentData);
        
        // Para este exemplo, vamos simular algumas datas disponíveis
        // Em uma implementação real, você carregaria datas e horários do Firebase
        const today = new Date();
        const dates = [];
        
        for (let i = 1; i <= 10; i++) {
          const date = new Date();
          date.setDate(today.getDate() + i);
          dates.push({
            value: date.toISOString().split('T')[0],
            label: formatDate(date.toISOString().split('T')[0])
          });
        }
        
        setAvailableDates(dates);
        setStatus('valid');
      } catch (error) {
        console.error('Erro ao validar token:', error);
        setStatus('error');
        setErrorMessage('Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente ou entre em contato com o estabelecimento.');
      }
    };
    
    validateAndLoad();
  }, [appointmentId, token]);
  
  // Atualizar horários disponíveis quando a data for alterada
  useEffect(() => {
    if (!selectedDate) {
      setAvailableTimes([]);
      return;
    }
    
    // Simular horários disponíveis
    // Em uma implementação real, você buscaria horários disponíveis do Firebase
    const times = [];
    for (let hour = 9; hour <= 18; hour++) {
      times.push({
        value: `${hour.toString().padStart(2, '0')}:00`,
        label: `${hour.toString().padStart(2, '0')}:00`
      });
      
      if (hour < 18) {
        times.push({
          value: `${hour.toString().padStart(2, '0')}:30`,
          label: `${hour.toString().padStart(2, '0')}:30`
        });
      }
    }
    
    setAvailableTimes(times);
  }, [selectedDate]);
  
  // Reagendar o agendamento
  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      alert('Por favor, selecione uma data e horário para reagendar.');
      return;
    }
    
    try {
      setStatus('loading');
      
      // Atualizar agendamento no Firebase
      const appointmentRef = doc(db, `salons/${appointmentId.split('/')[0]}/appointments/${appointmentId}`);
      await updateDoc(appointmentRef, {
        data: selectedDate,
        horario: selectedTime,
        reagendado: true,
        dataReagendamento: Timestamp.now()
      });
      
      setStatus('rescheduled');
    } catch (error) {
      console.error('Erro ao reagendar:', error);
      setStatus('error');
      setErrorMessage('Não foi possível reagendar. Por favor, entre em contato com o estabelecimento.');
    }
  };
  
  // Renderizar mensagem de carregamento
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen bg-gray-100 justify-center items-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando informações do agendamento...</p>
        </div>
      </div>
    );
  }
  
  // Renderizar erro ou link inválido
  if (status === 'invalid' || status === 'error') {
    return (
      <div className="flex min-h-screen bg-gray-100 justify-center items-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 rounded-full p-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
            Não foi possível processar sua solicitação
          </h2>
          <p className="text-gray-600 mb-6 text-center">
            {errorMessage}
          </p>
          <div className="text-center">
            <a href="/" className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors">
              Voltar para página inicial
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  // Renderizar agendamento reagendado com sucesso
  if (status === 'rescheduled') {
    return (
      <div className="flex min-h-screen bg-gray-100 justify-center items-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 rounded-full p-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
            Agendamento Reagendado!
          </h2>
          <div className="border-b border-gray-200 pb-4 mb-4">
            <p className="text-gray-600 text-center">
              Seu agendamento foi reagendado com sucesso para:
            </p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="font-medium text-gray-800">
              {appointment?.cliente || 'Cliente'}
            </p>
            <p className="text-gray-600">
              <span className="font-medium">Nova Data:</span> {formatDate(selectedDate)}
            </p>
            <p className="text-gray-600">
              <span className="font-medium">Novo Horário:</span> {selectedTime}
            </p>
            <p className="text-gray-600">
              <span className="font-medium">Serviço:</span> {
                Array.isArray(appointment?.servicos) && appointment?.servicos
                  .map(s => typeof s === 'object' && s.nome ? s.nome : 'Serviço')
                  .join(', ')
              }
            </p>
            <p className="text-gray-600">
              <span className="font-medium">Profissional:</span> {
                typeof appointment?.profissional === 'object' 
                  ? appointment?.profissional.nome 
                  : appointment?.profissional || 'Não informado'
              }
            </p>
          </div>
          
          <div className="text-center">
            <a href="/" className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors">
              Voltar para página inicial
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  // Renderizar formulário de reagendamento
  return (
    <div className="flex min-h-screen bg-gray-100 justify-center items-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
          Reagendar Agendamento
        </h2>
        
        {appointment && (
          <div className="mb-6">
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="font-medium text-gray-800">
                {appointment.cliente || 'Cliente'}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Data Atual:</span> {formatDate(appointment.data)}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Horário Atual:</span> {appointment.horario?.substring(0, 5) || 'Não informado'}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Serviço:</span> {
                  Array.isArray(appointment.servicos) && appointment.servicos
                    .map(s => typeof s === 'object' && s.nome ? s.nome : 'Serviço')
                    .join(', ')
                }
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Profissional:</span> {
                  typeof appointment.profissional === 'object' 
                    ? appointment.profissional.nome 
                    : appointment.profissional || 'Não informado'
                }
              </p>
            </div>
            
            <p className="text-gray-600 mb-4 text-center">
              Escolha uma nova data e horário para seu agendamento:
            </p>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                  Nova Data
                </label>
                <select
                  id="date"
                  className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                >
                  <option value="">Selecione uma data</option>
                  {availableDates.map((date) => (
                    <option key={date.value} value={date.value}>
                      {date.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                  Novo Horário
                </label>
                <select
                  id="time"
                  className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  disabled={!selectedDate}
                >
                  <option value="">Selecione um horário</option>
                  {availableTimes.map((time) => (
                    <option key={time.value} value={time.value}>
                      {time.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="pt-4">
                <button
                  onClick={handleReschedule}
                  disabled={!selectedDate || !selectedTime}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirmar Reagendamento
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReschedulePage; 